// Frontend-side mirror of the backend portfolio_readiness_service reason codes.
// Keep in sync with functions/services/portfolio_readiness_service.py.

export const READINESS_STATUS_READY = 'ready';
export const READINESS_STATUS_PENDING = 'pending';
export const READINESS_STATUS_FAILED = 'failed';
export const READINESS_STATUS_EXCLUDED = 'excluded';

export const REASON_CODE_MISSING_PRICE = 'missing_price';
export const REASON_CODE_WAITING_MARKET_REGISTRATION = 'waiting_market_registration';
export const REASON_CODE_MISSING_FACTOR_EXPOSURE = 'missing_factor_exposure';
export const REASON_CODE_MISSING_IDIO_VOL = 'missing_idio_vol';
export const REASON_CODE_IDENTITY_UNRESOLVED = 'identity_unresolved';
export const REASON_CODE_MARKET_REGISTRATION_FAILED = 'market_registration_failed';

const REASON_LABELS = {
    [REASON_CODE_MISSING_PRICE]: 'Missing price',
    [REASON_CODE_WAITING_MARKET_REGISTRATION]: 'Waiting for market data',
    [REASON_CODE_MISSING_FACTOR_EXPOSURE]: 'Missing factor exposure',
    [REASON_CODE_MISSING_IDIO_VOL]: 'Missing idio vol',
    [REASON_CODE_IDENTITY_UNRESOLVED]: 'Identity unresolved',
    [REASON_CODE_MARKET_REGISTRATION_FAILED]: 'Market registration failed',
};

export function getReadinessReasonLabel(reason) {
    if (!reason) return '';
    if (typeof reason === 'string') {
        return REASON_LABELS[reason] || reason;
    }
    if (typeof reason.label === 'string' && reason.label.trim()) {
        return reason.label;
    }
    if (typeof reason.code === 'string') {
        return REASON_LABELS[reason.code] || reason.code;
    }
    return '';
}

export function getReadinessStatus(row) {
    if (!row) return READINESS_STATUS_PENDING;
    const status = row.readiness_status;
    if (typeof status !== 'string') {
        return row.included_in_risk === true ? READINESS_STATUS_READY : READINESS_STATUS_PENDING;
    }
    return STATUS_SEVERITY_ORDER.includes(status) ? status : READINESS_STATUS_PENDING;
}

export function isRowIncludedInRisk(row) {
    if (!row) return false;
    if (row.included_in_risk === true) return true;
    if (row.included_in_risk === false) return false;
    return getReadinessStatus(row) === READINESS_STATUS_READY;
}

export function getReadinessReasons(row) {
    const reasons = row?.readiness_reasons;
    return Array.isArray(reasons) ? reasons : [];
}

const STATUS_LABELS = {
    [READINESS_STATUS_READY]: 'Included in risk',
    [READINESS_STATUS_PENDING]: 'Waiting for data',
    [READINESS_STATUS_FAILED]: 'Cannot include',
    [READINESS_STATUS_EXCLUDED]: 'Excluded',
};

export function getReadinessStatusLabel(row) {
    return STATUS_LABELS[getReadinessStatus(row)] || STATUS_LABELS[READINESS_STATUS_READY];
}

const STATUS_COLORS = {
    [READINESS_STATUS_READY]: 'success',
    [READINESS_STATUS_PENDING]: 'info',
    [READINESS_STATUS_FAILED]: 'error',
    [READINESS_STATUS_EXCLUDED]: 'warning',
};

export function getReadinessStatusColor(row) {
    return STATUS_COLORS[getReadinessStatus(row)] || 'default';
}

export function formatCoverageBannerText(coverage) {
    if (!coverage || !Number.isFinite(Number(coverage.total_positions))) {
        return '';
    }
    const total = Number(coverage.total_positions) || 0;
    const included = Number(coverage.included_count) || 0;
    const pending = Number(coverage.pending_count) || 0;
    const excluded = Number(coverage.excluded_count) || 0;
    const failed = Number(coverage.failed_count) || 0;
    const notIncluded = pending + excluded + failed;
    if (total === 0 || notIncluded === 0) {
        return '';
    }
    const parts = [`Risk metrics currently include ${included} of ${total} positions.`];
    if (pending > 0) {
        parts.push(`${pending} ${pending === 1 ? 'is' : 'are'} waiting for data.`);
    }
    if (failed > 0) {
        parts.push(`${failed} could not be included.`);
    }
    if (excluded > 0) {
        parts.push(`${excluded} ${excluded === 1 ? 'is' : 'are'} excluded from calculations.`);
    }
    return parts.join(' ');
}

// Order of severity — the LATER the entry, the more conservative it is. When
// calc-time and boundary-time readiness disagree we always pick the more
// conservative status so we never accidentally mark a row "ready" that
// upstream signals said was pending/failed/excluded.
const STATUS_SEVERITY_ORDER = [
    READINESS_STATUS_READY,
    READINESS_STATUS_PENDING,
    READINESS_STATUS_EXCLUDED,
    READINESS_STATUS_FAILED,
];

function statusSeverityRank(status) {
    if (typeof status !== 'string') return -1;
    return STATUS_SEVERITY_ORDER.indexOf(status);
}

function normalizeStatus(status) {
    const normalized = typeof status === 'string' ? status.trim() : '';
    return STATUS_SEVERITY_ORDER.includes(normalized)
        ? normalized
        : READINESS_STATUS_PENDING;
}

function normalizeReasonList(reasons) {
    if (!Array.isArray(reasons)) return [];
    return reasons.filter((reason) => reason && (reason.code || reason.label));
}

function mergeReasonLists(...lists) {
    const seen = new Set();
    const merged = [];
    lists.forEach((list) => {
        (list || []).forEach((reason) => {
            const key = reason?.code || reason?.label;
            if (!key || seen.has(key)) return;
            seen.add(key);
            merged.push(reason);
        });
    });
    return merged;
}

/**
 * Combine multiple readiness snapshots for the same position and return the
 * most conservative view. Sources are processed in order; if any source says
 * pending/failed/excluded, that wins over a "ready" signal from another
 * source. Reasons from all sources are merged (de-duped by code).
 *
 * Each source is either `{ readiness_status, included_in_risk, readiness_reasons }`
 * (typically from an API response) or `null`/`undefined`.
 */
export function combineReadinessSources(...sources) {
    const cleanedSources = sources.filter(Boolean);
    if (!cleanedSources.length) {
        return {
            readiness_status: READINESS_STATUS_PENDING,
            included_in_risk: false,
            readiness_reasons: [],
        };
    }

    let bestStatus = READINESS_STATUS_READY;
    let bestRank = statusSeverityRank(READINESS_STATUS_READY);
    let anyExplicitPending = false;
    const reasonLists = [];

    cleanedSources.forEach((source) => {
        const status = normalizeStatus(source.readiness_status);
        const rank = statusSeverityRank(status);
        if (rank > bestRank) {
            bestRank = rank;
            bestStatus = status;
        }
        if (source.included_in_risk === false) {
            anyExplicitPending = true;
        }
        reasonLists.push(normalizeReasonList(source.readiness_reasons));
    });

    const mergedReasons = mergeReasonLists(...reasonLists);
    const included = !anyExplicitPending && bestStatus === READINESS_STATUS_READY;
    return {
        readiness_status: bestStatus,
        included_in_risk: included,
        readiness_reasons: mergedReasons,
    };
}

export function hasPendingRiskCoverage(coverage) {
    if (!coverage) return false;
    const pending = Number(coverage.pending_count) || 0;
    return pending > 0 || coverage.has_pending === true;
}
